/*
 *
 * OktaVerification action/reducer
 *
 */

import { createSlice, createAction, PayloadAction } from "@reduxjs/toolkit";
import { push } from "connected-react-router";
import { createActionDispatchers } from "utils/reduxHelpers";
import { mfaFlow } from "utils/enum";
import {
  QRCodeRequestPayload,
  QRCodeResponse,
  StatusType,
  SubmissionStateType,
} from "./types";
import { AuthenticatorStatusEnum, PassCodeSubmissionStatusEnum } from "./enums";

export type State = {
  loadingQRLink: boolean;
  loadingPassCodeSubmission: SubmissionStateType;
  loadingStatus: boolean;

  showError: boolean;

  qrCodeLink: string;
  secretCode: string;
  status: StatusType;
  currentFlow: mfaFlow;
};

export const initialState: State = {
  loadingQRLink: false,
  loadingPassCodeSubmission: PassCodeSubmissionStatusEnum.INITIATE,
  loadingStatus: false,

  showError: false,

  qrCodeLink: "",
  secretCode: "",
  status: AuthenticatorStatusEnum.INITIAL,
  currentFlow: mfaFlow.SECURITY,
};

const slice = createSlice({
  name: "oktaVerification",
  initialState,
  // create actions with reducer (slice action) here
  reducers: {
    sampleAction() {
      // mutate state
    },
    loadQRCode(state: State, action: PayloadAction<QRCodeRequestPayload>) {
      state.loadingQRLink = true;
      state.currentFlow = action.payload.currentLayout;
    },
    loadStatus(state: State) {
      state.loadingStatus = false;
    },
    setError(state: State, action: PayloadAction<boolean>) {
      state.showError = action.payload;
    },
    setStatus(state: State, action: PayloadAction<StatusType>) {
      state.status = action.payload;
    },
    reset(state: State) {
      state.status = AuthenticatorStatusEnum.INITIAL;
      state.loadingPassCodeSubmission = PassCodeSubmissionStatusEnum.INITIATE;
      state.qrCodeLink = "";
      state.showError = false;
      state.secretCode = "";
    },
    resetSubmissionStatus(state: State) {
      state.loadingPassCodeSubmission = PassCodeSubmissionStatusEnum.INITIATE;
      state.showError = false;
      state.status = AuthenticatorStatusEnum.INITIAL;
    },
    afterOktaAuthenticatorFetch(
      state: State,
      action: PayloadAction<QRCodeResponse>
    ) {
      state.loadingQRLink = false;
      state.qrCodeLink = action.payload.qrCodeLink;
      state.secretCode = action.payload.secretCode;
    },
    submitPassCode(state: State) {
      state.loadingPassCodeSubmission = PassCodeSubmissionStatusEnum.INITIATE;
    },
    afterPassCodeSubmission(
      state: State,
      action: PayloadAction<SubmissionStateType>
    ) {
      state.loadingPassCodeSubmission = action.payload;
    },
    afterStatusFetch(state: State, action: PayloadAction<StatusType>) {
      state.status = action.payload;
    },
  },
  // reducer for external action
  // extraReducers: (builder) => {
  // builder.addCase(externalAction, (state) => {
  // });
  // },
});

/**
 * create actions WITHOUT reducer (pure action) here
 */
export const pureAction = createAction("oktaVerification/pureAction");

/**
 * actions from slice to be exported for saga
 */
export const {
  sampleAction,
  afterOktaAuthenticatorFetch,
  loadQRCode,
  submitPassCode,
  setError,
  afterPassCodeSubmission,
  afterStatusFetch,
  loadStatus,
  setStatus,
  reset,
  resetSubmissionStatus,
} = slice.actions;

/**
 * all actions for layout
 */
export const actionProps = createActionDispatchers(
  slice.actions,
  // slice actions
  [
    "sampleAction",
    "loadQRCode",
    "submitPassCode",
    "loadStatus",
    "reset",
    "resetSubmissionStatus",
  ],
  // pure actions
  {
    pureAction,
    goToMFAScreen: (redirect) => push(redirect),
  }
);

export default slice.reducer;
